import { useEffect } from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import 'src/core/utils/i18n';
import { enableFreeze } from 'react-native-screens';
import { LogBox } from 'react-native';
import { initialWindowMetrics, SafeAreaProvider } from 'react-native-safe-area-context';
import Navigation from 'src/presentation/navigation/Navigation';
import Updater from 'src/presentation/helpers/Updater';
import ApiErrorToast from 'src/presentation/components/molecules/ApiErrorToast';
import AlertDialog from 'src/presentation/dialogs/AlertDialog';
import OfflineBanner from 'src/presentation/components/molecules/OfflineBanner';
import MigrationGate from 'src/presentation/components/organisms/MigrationGate';
import AppViewport from 'src/presentation/components/layout/AppViewport';
import useCachedResources from 'src/presentation/hooks/useCachedResources';
import { installDrawerEscapeBridge } from 'src/presentation/helpers/DrawerBridge';
import { sendUnembeddedVisitorHome } from 'src/presentation/helpers/EmbedGuard';
import { SplashScreenService } from 'src/data/services/Services';

LogBox.ignoreAllLogs();

// Before anything renders, so a direct hit on the app host does not flash a screen outside the
// site first. Does nothing on native, and nothing inside the panel.
sendUnembeddedVisitorHome();

// Freeze off-screen native-stack screens (react-freeze). This stops background screens from
// re-rendering while store updates fire during the login event-refresh, which on the New
// Architecture (Fabric) otherwise crashes with "addViewAt: child already has a parent".
enableFreeze(true);

SplashScreenService.preventAutoHide().catch(() => {});

function App() {
	const resourcesLoaded = useCachedResources();

	// Only does anything on web inside the drawer: it lets Escape close the panel even while
	// the visitor is focused inside the app.
	useEffect(() => installDrawerEscapeBridge(), []);

	if (!resourcesLoaded) return null;

	SplashScreenService.hideAsync().catch(() => {});

	return (
		<GestureHandlerRootView style={{ flex: 1 }}>
			{/* System-bar icon styling (status + navigation) is driven per screen by NavBar via
			    SystemBars.setStyle, so the colours follow each screen's header. */}
			<MigrationGate>
				<SafeAreaProvider initialMetrics={initialWindowMetrics}>
					{/* On web this boxes the app into a centred column; on native it renders its children
					    unchanged. It wraps the navigator so the tab bar sits inside the column, and the
					    toast with it, since that is an absolutely positioned overlay. */}
					<AppViewport>
						<Updater />
						{/* Above the navigator, so being offline is visible on every screen instead of only
						    on the home screen. */}
						<OfflineBanner />
						<Navigation />
						<ApiErrorToast />
						{/* Alerts and confirmations on web: react-native-web's Alert is an empty function, so
						    these have to be rendered. Native keeps using the system dialog and this renders
						    null there. */}
						<AlertDialog />
					</AppViewport>
				</SafeAreaProvider>
			</MigrationGate>
		</GestureHandlerRootView>
	);
}

export default App;
